import type { SupplierStatus } from '@tandemone/types'
import { EVENTS } from '@tandemone/types'
import { useEffect } from 'react'

import { track } from '@/domains/observability'

type Params = {
  viewedSupplierId: string | null
  viewedCompanyId: string | null
  viewedCompanySlug: string | null
  viewedCompanyName: string | null
  viewedStatus: SupplierStatus | null
}

/** fires `supplier_profile_viewed` once on unmount or pagehide, whichever
 * comes first. the `fired` flag guards against both paths running.
 */
export function useSupplierProfileViewTracking({
  viewedSupplierId,
  viewedCompanyId,
  viewedCompanySlug,
  viewedCompanyName,
  viewedStatus
}: Params): void {
  useEffect(() => {
    if (
      !viewedSupplierId ||
      !viewedCompanyId ||
      !viewedCompanySlug ||
      !viewedCompanyName ||
      !viewedStatus
    ) {
      return
    }

    const startedAt = performance.now()
    let fired = false

    function fire() {
      if (fired) return
      fired = true
      track(EVENTS.SUPPLIER_PROFILE_VIEWED, {
        viewed_supplier_id: viewedSupplierId!,
        viewed_company_id: viewedCompanyId!,
        viewed_company_slug: viewedCompanySlug!,
        viewed_company_name: viewedCompanyName!,
        viewed_status: viewedStatus!,
        duration_ms: Math.round(performance.now() - startedAt)
      })
    }

    window.addEventListener('pagehide', fire)
    return () => {
      window.removeEventListener('pagehide', fire)
      fire()
    }
  }, [
    viewedSupplierId,
    viewedCompanyId,
    viewedCompanySlug,
    viewedCompanyName,
    viewedStatus
  ])
}
